// To parse this data:
//
//   import { Convert, Graphooper } from "./file";
//
//   const graphooper = Convert.toGraphooper(json);
//
// These functions will throw an error if the JSON doesn't
// match the expected interface, even if the JSON is valid.
import { LineStringFeature } from "./LineStringFeature";
import { GraphhopperInstruction } from "./turnInstructions";
import {MarkerFeature, setElevationData} from "./MarkerFeature";
import {plainToClass} from "class-transformer";

export interface Graphooper {
  hints: Hints;
  info: Info;
  paths: Path[];
}

export interface Hints {
  "visited_nodes.sum": number;
  "visited_nodes.average": number;
}

export interface Info {
  copyrights: string[];
  took: number;
}

export interface Path {
  distance: number;
  weight: number;
  time: number;
  transfers: number;
  points_encoded: boolean;
  bbox: number[];
  points: Points;
  legs: any[];
  details: Details;
  ascend: number;
  descend: number;
  snapped_waypoints: Points;
  /**
   * Turns for the geometry GraphHopper just produced. Kept so a recording can be guided
   * along this segment later — exact by construction, because they describe coordinates
   * GraphHopper generated rather than a route matched back to them afterwards.
   */
  instructions?: GraphhopperInstruction[];
}

export interface Details {
  distance: Array<number[]>;
  surface: Array<Array<number | string>>;
}

export interface Points {
  type: string;
  coordinates: Array<number[]>;
}

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
export class Convert {
  public static toGraphooper(json: string): Graphooper {
    const graphooper: Graphooper = JSON.parse(json)
    return graphooper
  }

  public static graphooperToJson(value: Graphooper): string {
    return JSON.stringify(uncast(value, r("Graphooper")), null, 2);
  }
}

function invalidValue(typ: any, val: any, key: any = ""): never {
  if (key) {
    throw Error(
      `Invalid value for key "${key}". Expected type ${JSON.stringify(
        typ
      )} but got ${JSON.stringify(val)}`
    );
  }
  throw Error(
    `Invalid value ${JSON.stringify(val)} for type ${JSON.stringify(typ)}`
  );
}

function jsonToJSProps(typ: any): any {
  if (typ.jsonToJS === undefined) {
    const map: any = {};
    typ.props.forEach((p: any) => (map[p.json] = { key: p.js, typ: p.typ }));
    typ.jsonToJS = map;
  }
  return typ.jsonToJS;
}

function jsToJSONProps(typ: any): any {
  if (typ.jsToJSON === undefined) {
    const map: any = {};
    typ.props.forEach((p: any) => (map[p.js] = { key: p.json, typ: p.typ }));
    typ.jsToJSON = map;
  }
  return typ.jsToJSON;
}

function transform(val: any, typ: any, getProps: any, key: any = ""): any {
  function transformPrimitive(typ: string, val: any): any {
    if (typeof typ === typeof val) return val;
    return invalidValue(typ, val, key);
  }

  function transformUnion(typs: any[], val: any): any {
    // val must validate against one typ in typs
    const l = typs.length;
    for (let i = 0; i < l; i++) {
      const typ = typs[i];
      try {
        return transform(val, typ, getProps);
      } catch (_) {}
    }
    return invalidValue(typs, val);
  }

  function transformEnum(cases: string[], val: any): any {
    if (cases.indexOf(val) !== -1) return val;
    return invalidValue(cases, val);
  }

  function transformArray(typ: any, val: any): any {
    // val must be an array with no invalid elements
    if (!Array.isArray(val)) return invalidValue("array", val);
    return val.map((el) => transform(el, typ, getProps));
  }

  function transformDate(val: any): any {
    if (val === null) {
      return null;
    }
    const d = new Date(val);
    if (isNaN(d.valueOf())) {
      return invalidValue("Date", val);
    }
    return d;
  }

  function transformObject(
    props: { [k: string]: any },
    additional: any,
    val: any
  ): any {
    if (val === null || typeof val !== "object" || Array.isArray(val)) {
      return invalidValue("object", val);
    }
    const result: any = {};
    Object.getOwnPropertyNames(props).forEach((key) => {
      const prop = props[key];
      const v = Object.prototype.hasOwnProperty.call(val, key)
        ? val[key]
        : undefined;
      result[prop.key] = transform(v, prop.typ, getProps, prop.key);
    });
    Object.getOwnPropertyNames(val).forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(props, key)) {
        result[key] = transform(val[key], additional, getProps, key);
      }
    });
    return result;
  }

  if (typ === "any") return val;
  if (typ === null) {
    if (val === null) return val;
    return invalidValue(typ, val);
  }
  if (typ === false) return invalidValue(typ, val);
  while (typeof typ === "object" && typ.ref !== undefined) {
    typ = typeMap[typ.ref];
  }
  if (Array.isArray(typ)) return transformEnum(typ, val);
  if (typeof typ === "object") {
    return typ.hasOwnProperty("unionMembers")
      ? transformUnion(typ.unionMembers, val)
      : typ.hasOwnProperty("arrayItems")
      ? transformArray(typ.arrayItems, val)
      : typ.hasOwnProperty("props")
      ? transformObject(getProps(typ), typ.additional, val)
      : invalidValue(typ, val);
  }
  // Numbers can be parsed by Date but shouldn't be.
  if (typ === Date && typeof val !== "number") return transformDate(val);
  return transformPrimitive(typ, val);
}

function cast<T>(val: any, typ: any): T {
  return transform(val, typ, jsonToJSProps);
}

function uncast<T>(val: T, typ: any): any {
  return transform(val, typ, jsToJSONProps);
}

function a(typ: any) {
  return { arrayItems: typ };
}

function u(...typs: any[]) {
  return { unionMembers: typs };
}

function o(props: any[], additional: any) {
  return { props, additional };
}

function m(additional: any) {
  return { props: [], additional };
}

function r(name: string) {
  return { ref: name };
}

const typeMap: any = {
  Graphooper: o(
    [
      { json: "hints", js: "hints", typ: r("Hints") },
      { json: "info", js: "info", typ: r("Info") },
      { json: "paths", js: "paths", typ: a(r("Path")) },
    ],
    false
  ),
  Hints: o(
    [
      { json: "visited_nodes.sum", js: "visited_nodes.sum", typ: 0 },
      { json: "visited_nodes.average", js: "visited_nodes.average", typ: 0 },
    ],
    false
  ),
  Info: o(
    [
      { json: "copyrights", js: "copyrights", typ: a("") },
      { json: "took", js: "took", typ: 0 },
    ],
    false
  ),
  Path: o(
    [
      { json: "distance", js: "distance", typ: 3.14 },
      { json: "weight", js: "weight", typ: 3.14 },
      { json: "time", js: "time", typ: 0 },
      { json: "transfers", js: "transfers", typ: 0 },
      { json: "points_encoded", js: "points_encoded", typ: true },
      { json: "bbox", js: "bbox", typ: a(3.14) },
      { json: "points", js: "points", typ: r("Points") },
      { json: "legs", js: "legs", typ: a("any") },
      { json: "details", js: "details", typ: r("Details") },
      { json: "ascend", js: "ascend", typ: 3.14 },
      { json: "descend", js: "descend", typ: 3.14 },
      { json: "snapped_waypoints", js: "snapped_waypoints", typ: r("Points") },
      { json: "instructions", js: "instructions", typ: u(undefined, a("any")) },
    ],
    false
  ),
  Details: o(
    [
      { json: "distance", js: "distance", typ: a(a(3.14)) },
      { json: "surface", js: "surface", typ: a(a(u(0, ""))) },
    ],
    false
  ),
  Points: o(
    [
      { json: "type", js: "type", typ: "" },
      { json: "coordinates", js: "coordinates", typ: a(a(3.14)) },
    ],
    false
  ),
};

export async function snapPoints(startMarker: MarkerFeature, endMarker: MarkerFeature, routePreference: String = "walk"): Promise<LineStringFeature> {
    const key = "8608e7b4-d550-43d2-b545-4a70cc59436b";
      try {
        const snapJson = await fetch(
          `https://graphhopper.com/api/1/route` +
          `?point=${startMarker.markerGeometry.coordinates[1]},${startMarker.markerGeometry.coordinates[0]}` +
          `&point=${endMarker.markerGeometry.coordinates[1]},${endMarker.markerGeometry.coordinates[0]}` +
          `&instructions=true&points_encoded=false` +
          `&details=surface&details=distance` +
          `&elevation=true` +
          `&profile=${routePreference}` +
          `&key=${key}`,
          {
            cache: "force-cache",
          }
        );
        let response = await snapJson.text();
        const graphooper = Convert.toGraphooper(response);
        await setElevationData(startMarker);
        // graphooper.paths[0].points.coordinates.push(endMarker.markerGeometry.coordinates) #Dont add end point.
        return graphooperToGeoJson(graphooper, routePreference)
      } catch (e) {
        throw e
      }
}

export function graphooperToGeoJson(graphooper: Graphooper, profile?: String): LineStringFeature {
    return LineStringFeature.fromGraphooper(graphooper, profile);
}
